import React, { useMemo } from 'react';
import {
    Platform,
    Pressable,
    StyleSheet,
    Text,
    View,
    type StyleProp,
    type TextStyle,
    type ViewStyle,
} from 'react-native';

import { getColors } from '@/core/constants/uiColors';

export type ButtonVariant = 'primary' | 'outline' | 'text';

export type ButtonProps = {
    title: string;
    onPress?: (() => void) | undefined;
    disabled?: boolean;
    variant?: ButtonVariant;
    theme?: 'light' | 'dark'; // Explicit theme override
    leading?: React.ReactNode;
    trailing?: React.ReactNode;
    fullWidth?: boolean;
    style?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    testID?: string;
    numberOfLines?: number;
};

export function Button({
    title,
    onPress,
    disabled = false,
    variant = 'primary',
    theme = 'light',
    leading,
    trailing,
    fullWidth = false,
    style,
    textStyle,
    testID,
    numberOfLines = 1,
}: ButtonProps) {

    const colors = useMemo(() => getColors(theme), [theme]);

    // Get contrasting text color for primary variant (memoized)
    const primaryTextColor = useMemo(() => {
        const oppositeTheme = theme === 'light' ? 'dark' : 'light';
        const explicitColors = getColors(oppositeTheme);
        return explicitColors.buttonDefault;
    }, [theme]);

    // Get icon color based on variant and state
    const iconColor = useMemo(() => {
        if (variant === 'primary') {
            return disabled ? colors.fontSecondary : primaryTextColor;
        } else if (variant === 'outline') {
            return disabled ? colors.buttonDisabled : colors.buttonDefault;
        } else {
            // text variant
            return disabled ? colors.buttonDisabled : colors.buttonDefault;
        }
    }, [variant, disabled, colors, primaryTextColor]);

    // Clone icon elements to apply color prop
    const leadingIcon = useMemo(() => {
        if (!leading) return null;
        if (React.isValidElement(leading)) {
            return React.cloneElement(leading as React.ReactElement<any>, { color: iconColor });
        }
        return leading;
    }, [leading, iconColor]);

    const trailingIcon = useMemo(() => {
        if (!trailing) return null;
        if (React.isValidElement(trailing)) {
            return React.cloneElement(trailing as React.ReactElement<any>, { color: iconColor });
        }
        return trailing;
    }, [trailing, iconColor]);

    return (
        <Pressable
            accessibilityRole="button"
            disabled={disabled}
            onPress={onPress}
            testID={testID}
            style={({ pressed }) => [
                styles.base,
                { height: 48 },
                fullWidth && styles.fullWidth,
                variant === 'text' && styles.textVariant,

                // Primary variant styles
                variant === 'primary' && { backgroundColor: colors.buttonDefault },
                variant === 'primary' && !disabled && pressed && { backgroundColor: colors.buttonPressed },
                variant === 'primary' && disabled && { backgroundColor: colors.buttonDisabled },

                // Outline variant styles  
                variant === 'outline' && {
                    backgroundColor: 'transparent',
                    borderWidth: 2,
                    borderColor: colors.buttonDefault
                },
                variant === 'outline' && !disabled && pressed && { borderColor: colors.buttonPressed },
                variant === 'outline' && disabled && { borderColor: colors.buttonDisabled },

                // Text variant styles
                variant === 'text' && { backgroundColor: 'transparent' },

                style,
            ]}
        >
            <View style={styles.content}>
                {leadingIcon ? <View style={styles.icon}>{leadingIcon}</View> : null}
                <Text
                    style={[
                        styles.label,
                        variant === 'primary' && { color: primaryTextColor },
                        variant === 'primary' && disabled && { color: colors.fontSecondary },

                        variant === 'outline' && { color: colors.buttonDefault },
                        variant === 'outline' && { textDecorationLine: 'none' as const },
                        disabled && variant === 'outline' && { color: colors.buttonDisabled },

                        variant === 'text' && {
                            color: colors.buttonDefault,
                            textDecorationLine: 'underline',
                            textDecorationStyle: 'solid',
                            textDecorationColor: colors.buttonDefault,
                            ...(Platform.OS === 'web'
                                ? ({ textUnderlineOffset: 4 } as object)
                                : null),
                        },
                        disabled && variant === 'text' && { color: colors.buttonDisabled },
                        textStyle,
                    ]}
                    numberOfLines={numberOfLines}
                >
                    {title}
                </Text>
                {trailingIcon ? <View style={styles.icon}>{trailingIcon}</View> : null}
            </View>
        </Pressable>
    );
}

const styles = StyleSheet.create({
    base: {
        paddingHorizontal: 12,
        borderRadius: 4,
        alignSelf: 'flex-start',
        justifyContent: 'center',
        alignItems: 'center',
    },
    fullWidth: {
        alignSelf: 'stretch',
    },
    textVariant: {
        // transparent background keeps the touch target large
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    icon: {
        marginHorizontal: 4,
        height: 20,
        justifyContent: 'center',
        alignItems: 'center',
    },
    label: {
        fontSize: 16,
        fontFamily: 'Matter-SemiBold',
        lineHeight: 20,
    },
    underlinedText: {
        textDecorationLine: 'underline',
    },
});

export default Button;


